import { useCallback, useEffect, useRef, useState } from 'react'

import { Backdrop, Handle, Sheet } from './styles'
import { DrawerProps } from './types'

/** Arrasto mínimo, em px, para o gesto contar como "fechar". */
const LIMIAR_FECHAR = 90

function Drawer({ onHide, dismissible = true, children }: DrawerProps) {
  const backdropRef = useRef<HTMLDivElement>(null)
  const inicioY = useRef<number | null>(null)

  const [deslocamento, setDeslocamento] = useState(0)
  const [arrastando, setArrastando] = useState(false)

  // Trava o scroll do body enquanto aberto: sem isso, arrastar o sheet rola a
  // pagina atras dele no iOS.
  useEffect(() => {
    const anterior = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = anterior
    }
  }, [])

  useEffect(() => {
    if (!dismissible) return undefined

    const aoTeclar = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onHide()
      }
    }

    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [dismissible, onHide])

  const aoTocarInicio = useCallback(
    (event: React.TouchEvent) => {
      if (!dismissible) return
      inicioY.current = event.touches[0].clientY
      setArrastando(true)
    },
    [dismissible],
  )

  const aoTocarMover = useCallback(
    (event: React.TouchEvent) => {
      if (!dismissible || inicioY.current === null) return

      const delta = event.touches[0].clientY - inicioY.current
      // Só para baixo: arrastar para cima não estica o sheet.
      setDeslocamento(delta > 0 ? delta : 0)
    },
    [dismissible],
  )

  const aoTocarFim = useCallback(
    () => {
      if (!dismissible || inicioY.current === null) return

      const percorrido = deslocamento
      inicioY.current = null
      setArrastando(false)
      setDeslocamento(0)

      if (percorrido >= LIMIAR_FECHAR) {
        onHide()
      }
    },
    [deslocamento, dismissible, onHide],
  )

  return (
    <Backdrop
      ref={backdropRef}
      data-testid="drawer-backdrop"
      onClick={event => {
        if (dismissible && event.target === backdropRef.current) {
          onHide()
        }
      }}
    >
      <Sheet
        role="dialog"
        aria-modal="true"
        data-testid="drawer-sheet"
        arrastando={arrastando}
        deslocamento={deslocamento}
      >
        {dismissible && (
          <Handle
            data-testid="drawer-handle"
            onTouchStart={aoTocarInicio}
            onTouchMove={aoTocarMover}
            onTouchEnd={aoTocarFim}
          />
        )}
        {children}
      </Sheet>
    </Backdrop>
  )
}

export default Drawer
